import { FC, useMemo, useState } from 'react';
import { useFormikContext } from 'formik';
import { IconButton, InputAdornment, InputLabel, OutlinedInput } from '@mui/material';
import { flip, shift, useFloating } from '@floating-ui/react-dom';
import { styled } from '@mui/material/styles';
import { useClickAway } from 'react-use';
import moment from 'moment';

import DateRangePicker, { DateRangeValue, InitialFocusedRange } from 'pages/Admin/SuperAdminDashboard/DateRangePicker';

import { ReactComponent as FieldCalendar } from 'assets/image/dashboard/field-calendar.svg';

const OutlinedTextField = styled(OutlinedInput)(() => ({
	height: '40px',
	fontFamily: 'Rubik, sans-serif',

	'.MuiOutlinedInput-notchedOutline': {
		top: '-5px',
		outline: 'none',
		border: '1px solid #C6CCFE !important',
		borderRadius: '31px',
	},
	input: {
		height: 'unset',
		padding: '8px 12px',
		fontSize: 18,
		fontStyle: 'normal',
		fontWeight: 400,
		lineHeight: '131%',
		letterSpacing: '-0.18px',
		cursor: 'pointer',
	},
}));

const InputLabelStyled = styled(InputLabel)(() => ({
	fontFamily: 'Rubik, sans-serif',
	fontSize: '16px',
	color: '#1C2F4C',
	lineHeight: 1.2,
	fontWeight: 500,
}));

type DatePickerRangeFieldProps = {
	name: 'fromDate' | 'toDate';
	title: string;
	externalRange?: DateRangeValue;
	initialFocusedRange?: InitialFocusedRange;
};

const DatePickerRangeField: FC<DatePickerRangeFieldProps> = ({
	name,
	title,
	externalRange,
	initialFocusedRange,
}: DatePickerRangeFieldProps) => {
	const { values, setValues } = useFormikContext<any>();
	const [open, setOpen] = useState(false);

	const { refs, floatingStyles } = useFloating({
		strategy: 'fixed',
		placement: 'left-start',
		middleware: [flip(), shift()],
	});

	useClickAway(refs.floating, () => setOpen(false));

	const onChange = (range: DateRangeValue) =>
		setValues({ ...values, fromDate: range.startDate ?? null, toDate: range.endDate ?? null });

	const value = useMemo(() => (!!values[name] ? moment(values[name]).format('MM/DD/YYYY') : null), [values, name]);

	return (
		<>
			<div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 8 }}>
				<InputLabelStyled htmlFor={name}>{title}</InputLabelStyled>
				<OutlinedTextField
					id={name}
					type="text"
					onClick={() => setOpen(prev => !prev)}
					endAdornment={
						<InputAdornment position="end">
							<IconButton edge="end">
								<FieldCalendar />
							</IconButton>
						</InputAdornment>
					}
					value={value || 'MM/DD/YYYY'}
					onFocus={e => e.preventDefault()}
					ref={refs.setReference}
					slotProps={{
						input: {
							onInput: e => e.preventDefault(),
							onChange: e => e.preventDefault(),
							onKeyDown: e => e.preventDefault(),
							onKeyUp: e => e.preventDefault(),
							onFocus: e => e.preventDefault(),
							onClick: e => (e.target as any).blur(),
							sx: {
								color: value ? '#1C2F4C' : '#5B768B',
							},
						},
					}}
				/>
			</div>

			{open ? (
				<div ref={refs.setFloating} style={{ ...floatingStyles, zIndex: 1000 }}>
					<DateRangePicker
						externalRange={externalRange}
						onChange={onChange}
						onClose={() => setOpen(false)}
						initialFocusedRange={initialFocusedRange}
					/>
				</div>
			) : null}
		</>
	);
};

export default DatePickerRangeField;
